import { createWebLocksDeviceLock, type DeviceLock, type DeviceLockHandle } from '@bcio-voice/call-client/core';

export type PhoneOwner = { windowId: string; kind: 'app' | 'tab'; account: string; busy: boolean; state: string };
export type OwnershipSnapshot = { status: 'checking' | 'owner' | 'observer' | 'unsupported'; owner: PhoneOwner | null; message: string | null };
export interface OwnershipBus {
  postMessage(message: unknown): void;
  onmessage: ((event: MessageEvent) => void) | null;
  close(): void;
}
export function installedApp(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches;
}

/** The profile lock precedes ALL server device mutations. Metadata is advisory; only Web Locks grant ownership. */
export class PhoneOwnership {
  private snapshot: OwnershipSnapshot = { status: 'checking', owner: null, message: null };
  private listeners = new Set<() => void>();
  private handle: DeviceLockHandle | null = null;
  private bus: OwnershipBus | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private running = false;
  private acquiring = false;
  private yielding = false;
  private deferredUntil = 0;
  private lastOwnerAt = 0;
  private meta: PhoneOwner;
  constructor(private options: {
    account: string; kind: 'app' | 'tab'; windowId?: string;
    lock?: DeviceLock | undefined; bus?: () => OwnershipBus | null;
    busy: () => boolean; releasePhone: (handoff?: boolean) => Promise<boolean | void>;
    now?: () => number;
  }) {
    this.meta = { account: options.account, kind: options.kind, windowId: options.windowId ?? crypto.randomUUID(), busy: false, state: 'starting' };
  }
  getSnapshot = (): OwnershipSnapshot => this.snapshot;
  subscribe = (listener: () => void) => { this.listeners.add(listener); return () => { this.listeners.delete(listener); }; };
  isOwner = () => this.running && !!this.handle && !this.yielding;
  private now() { return this.options.now?.() ?? Date.now(); }
  private set(status: OwnershipSnapshot['status'], owner: PhoneOwner | null, message: string | null = null) {
    this.snapshot = { status, owner, message };
    for (const listener of this.listeners) listener();
  }
  start(): void {
    if (this.running) return;
    this.running = true;
    if (!this.options.lock) { this.set('unsupported', null, 'Use Chrome or Edge with Web Locks support to connect this phone.'); return; }
    this.bus = this.options.bus?.() ?? null;
    if (this.bus) this.bus.onmessage = event => { void this.receive(event.data); };
    this.bus?.postMessage({ type: 'query' });
    void this.acquire();
    this.timer = setInterval(() => {
      if (this.isOwner()) this.publish();
      else {
        if (this.snapshot.owner && this.now() - this.lastOwnerAt > 12000) this.set('observer', null);
        void this.acquire();
        this.bus?.postMessage({ type: 'query' });
      }
    }, 2000);
  }
  private async acquire() {
    if (!this.running || this.handle || this.acquiring || this.yielding || this.now() < this.deferredUntil || !this.options.lock) return;
    this.acquiring = true;
    try {
      const handle = await this.options.lock.acquire({ steal: false });
      if (!this.running) { handle?.release(); return; }
      if (!handle) { if (this.snapshot.status === 'checking') this.set('observer', this.snapshot.owner); return; }
      this.handle = handle;
      this.yielding = false;
      this.publish();
      void handle.lost.then(async () => {
        if (this.handle !== handle) return;
        this.handle = null;
        this.set('observer', null);
        await this.options.releasePhone();
      });
    } catch { this.set('unsupported', null, 'This browser could not reserve the phone. Close other phone windows and reopen Sidetone.'); }
    finally { this.acquiring = false; }
  }
  update(state: string): void { this.meta = { ...this.meta, state, busy: this.options.busy() }; if (this.isOwner()) this.publish(); }
  private publish() {
    this.meta = { ...this.meta, busy: this.options.busy() };
    this.set('owner', this.meta);
    this.bus?.postMessage({ type: 'owner', owner: this.meta });
  }
  requestHandoff(): void {
    if (this.isOwner()) return;
    if (this.meta.kind !== 'app' && this.snapshot.owner?.kind !== 'tab') { this.set('observer', this.snapshot.owner, 'Open the installed Sidetone app to move the phone there, or close the other phone window.'); return; }
    this.set('observer', this.snapshot.owner, 'Requesting the phone. The other window must be idle.');
    this.bus?.postMessage({ type: 'handoff', target: this.meta });
    void this.acquire();
  }
  private async receive(data: unknown) {
    if (!this.running || !data || typeof data !== 'object') return;
    const message = data as { type?: string; owner?: PhoneOwner; target?: PhoneOwner };
    if (message.type === 'query' && this.isOwner()) this.publish();
    if (message.type === 'owner' && !this.handle) {
      const owner = message.owner;
      if (!owner || typeof owner.windowId !== 'string' || !['app', 'tab'].includes(owner.kind) || typeof owner.account !== 'string') return;
      this.lastOwnerAt = this.now();
      this.set('observer', owner);
    }
    if (message.type === 'released') { void this.acquire(); this.bus?.postMessage({ type: 'query' }); }
    const target = message.target;
    if (message.type !== 'handoff' || !target || (target.kind !== 'app' && this.meta.kind !== 'tab') || typeof target.windowId !== 'string' || target.account !== this.meta.account) return;
    if (!this.handle && target.windowId !== this.meta.windowId) this.deferredUntil = this.now() + 8000;
    if (!this.isOwner()) return;
    if (this.options.busy()) { this.bus?.postMessage({ type: 'owner', owner: { ...this.meta, busy: true } }); return; }
    // Freeze new local calls synchronously before awaiting UNREGISTER/heartbeat completion.
    this.yielding = true;
    this.set('owner', this.meta, 'Moving the idle phone to the Sidetone app.');
    try {
      if (await this.options.releasePhone(true) === false) {
        this.yielding = false;
        this.publish();
        return;
      }
    } catch {
      this.yielding = false;
      this.set('owner', this.meta, 'The phone could not move safely. Keep using this window.');
      return;
    }
    this.deferredUntil = this.now() + 8000;
    this.handle?.release();
    this.handle = null;
    this.yielding = false;
    this.set('observer', null);
    this.bus?.postMessage({ type: 'released' });
  }
  async stop(): Promise<void> {
    this.running = false;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.handle) await this.options.releasePhone();
    this.handle?.release();
    this.handle = null;
    this.bus?.postMessage({ type: 'released' });
    this.bus?.close();
    this.bus = null;
    this.set('checking', null);
  }
}

export function browserPhoneOwnership(account: string, busy: () => boolean, releasePhone: (handoff?: boolean) => Promise<boolean | void>): PhoneOwnership {
  return new PhoneOwnership({ account, kind: installedApp() ? 'app' : 'tab', busy, releasePhone,
    lock: createWebLocksDeviceLock('sidetone-profile-phone-v2'),
    bus: () => typeof BroadcastChannel === 'undefined' ? null : new BroadcastChannel('sidetone-phone-owner-v2'),
  });
}
